import React from "react";
import { ShieldPlus, Syringe, Activity, CheckCircle2, Eye, AlertTriangle } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { hepatitisTypes, hepatitisFaqs, clinic } from "../data/mock";

const causes = ["Unsafe blood transfusion", "Shared or unsterilized needles", "Unprotected contact", "Mother-to-child transmission", "Contaminated food & water", "Unhygienic tattoos/piercings"];
const symptoms = ["Fatigue & weakness", "Yellow eyes & skin (jaundice)", "Dark urine", "Loss of appetite & nausea", "Abdominal pain", "Joint pain & mild fever"];

const Hepatitis = () => {
  return (
    <div>
      <SEO path="/hepatitis-treatment-varanasi" faqs={hepatitisFaqs} />
      <PageHero breadcrumb="Hepatitis Treatment"
        title="Hepatitis Treatment in Varanasi"
        subtitle="Expert diagnosis and antiviral treatment for Hepatitis B, Hepatitis C and other viral hepatitis infections." />

      <section className="py-16 sm:py-20">
        <div className="container-app max-w-4xl">
          <SectionHeading eyebrow="Overview" title="What is Hepatitis?" />
          <div className="space-y-4 text-slate-600 leading-relaxed text-center">
            <p>Hepatitis is inflammation of the liver, most commonly caused by viral infections (Hepatitis A, B, C and E) but also by alcohol, certain medicines and autoimmune conditions. It can be short-term (acute), resolving within weeks, or long-term (chronic), lasting for months or years.</p>
            <p>Chronic hepatitis — especially Hepatitis B and C — is particularly concerning because it can silently damage the liver over many years, gradually leading to fibrosis, cirrhosis and even liver cancer, often without any obvious symptoms in the early stages. This is why timely testing is so important, particularly for those at higher risk.</p>
            <p>The good news is that hepatitis is highly manageable. Hepatitis B can be effectively controlled with antiviral medicines, and Hepatitis C is now <strong className="text-slate-800">curable in the vast majority of patients</strong> with modern direct-acting antiviral therapy. Early testing, vaccination and specialist care from {clinic.doctorName} make all the difference in protecting your liver.</p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Types" title="Types of Viral Hepatitis" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hepatitisTypes.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <span className="inline-block text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full mb-4">{t.tag}</span>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{t.name}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading center={false} eyebrow="Causes & Transmission" title="How Hepatitis Spreads" />
            <div className="space-y-3">
              {causes.map((c, i) => (
                <div key={i} className="flex items-center gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-5 py-4">
                  <AlertTriangle size={20} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Symptoms" title="Common Symptoms" />
            <div className="space-y-3">
              {symptoms.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4 shadow-sm">
                  <Eye size={20} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnosis" title="How Hepatitis is Diagnosed"
            subtitle="Accurate testing confirms the type, checks how active the virus is and assesses liver health." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { t: "Viral Markers (HBsAg, Anti-HCV)", d: "Blood tests that detect the presence of Hepatitis B or C infection." },
              { t: "Viral Load (PCR)", d: "Measures how much virus is in the blood to guide treatment decisions." },
              { t: "Liver Function Test", d: "Assesses liver inflammation and how well the liver is functioning." },
              { t: "FibroScan / Ultrasound", d: "Checks for liver fibrosis, scarring and structural changes." },
            ].map((c, i) => (
              <div key={i} className="bg-sky-50/60 rounded-2xl p-6 border border-sky-100">
                <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-semibold text-slate-900 mt-2 mb-2 text-[15px]">{c.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Treatment & Prevention" title="How Hepatitis is Treated" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: ShieldPlus, t: "Antiviral Therapy", d: "Effective medicines to suppress Hepatitis B and cure Hepatitis C in most patients." },
              { icon: Syringe, t: "Vaccination", d: "Safe, effective vaccines available for Hepatitis A and B to protect you and your family." },
              { icon: Activity, t: "Long-Term Monitoring", d: "Regular LFT, viral load and FibroScan checks to track liver health over time." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-sky-500 text-white grid place-items-center mb-5"><c.icon size={24} /></div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-sky-50 border border-sky-100 rounded-2xl p-7 max-w-4xl mx-auto">
            <h3 className="font-heading font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2"><CheckCircle2 size={22} className="text-sky-500" /> Why Choose Dr. Indresh Dixit</h3>
            <p className="text-slate-600 text-sm leading-relaxed">With {clinic.experience} years of experience and access to advanced diagnostics, {clinic.doctorName} provides accurate hepatitis testing, personalized antiviral treatment and dedicated long-term follow-up — helping patients protect their liver and live healthy lives.</p>
          </div>
        </div>
      </section>

      <FAQSection faqs={hepatitisFaqs} title="Hepatitis — Frequently Asked Questions" />
      <AppointmentCTA title="Get Tested & Treated for Hepatitis Today" />
    </div>
  );
};

export default Hepatitis;
